import React, { useEffect, useState, useCallback, useRef } from 'react';
import moment from "moment";
import { message, Table, Dropdown, Menu, Button, Icon, Select, Input } from "antd";
import { platformApi } from "../../../../api"
import WhatsappMessageModal from './WhatsappMessageModal/whatsappMessageModal';

const { Option } = Select;

const DEFAULT_MODULES = [
    "Authentication",
    "Booking",
    "BookingReceipt",
    "CounterSale",
    "CustomerSpareOrderReceipts",
    "JobCard",
    "JobCardReceipts",
    "JobInvoiceReceipts",
    "NumberPlate",
    "Promotions",
    "Quotation",
    "Registration",
    "SaleInvoice",
    "ServiceReminder",
    "WhatsappWeb",
];

const STATUS_OPTIONS = [
    { value: "sent", label: "Sent" },
    { value: "delivered", label: "Delivered" },
    { value: "read", label: "Read" },
    { value: "failed", label: "Failed" },
];

const whatsappHistoryLogs = () => {

    const [data, setData] = useState([])
    const [spinning, setSpinning] = useState(true);
    const [limit, setLimit] = useState(10)
    const [page, setPage] = useState(1)
    const [count, setCount] = useState(0)
    const [visible, setVisible] = useState(false)
    const [selectedMessage, setSelectedMessage] = useState("")
    const [modules, setModules] = useState(DEFAULT_MODULES)

    const [filterModule, setFilterModule] = useState(undefined)
    const [filterStatus, setFilterStatus] = useState(undefined)
    const [filterMobileNo, setFilterMobileNo] = useState("")
    const [debouncedMobileNo, setDebouncedMobileNo] = useState("")
    const mobileDebounceRef = useRef(null)

    const fetchLogs = useCallback((p = page, l = limit, filters = {}) => {
        setSpinning(true)
        const params = new URLSearchParams()
        params.set("page", String(p))
        params.set("size", String(l))
        if (filters.module) params.set("module", filters.module)
        if (filters.mobileNo) params.set("mobileNo", filters.mobileNo)
        if (filters.status) params.set("status", filters.status)

        platformApi.get(`/api/whatsapp/whatsappLogStatus?${params.toString()}`)
            .then(response => {
                if (response.data.code === 200) {
                    const payload = response.data.response.data || {}
                    // Support both paginated { logs, count } and legacy array responses
                    if (Array.isArray(payload)) {
                        setData(payload)
                        setCount(payload.length)
                    } else {
                        setData(payload.logs || [])
                        setCount(payload.count || 0)
                        if (Array.isArray(payload.modules) && payload.modules.length > 0) {
                            setModules(payload.modules)
                        }
                    }
                } else {
                    message.error("Unable to fetch whstapp logs", 2)
                }
            })
            .catch(() => {
                message.error("Unable to fetch whstapp logs", 2)
            })
            .finally(() => setSpinning(false))
    }, [])

    useEffect(() => {
        if (mobileDebounceRef.current) clearTimeout(mobileDebounceRef.current)
        mobileDebounceRef.current = setTimeout(() => {
            setDebouncedMobileNo(filterMobileNo.trim())
            setPage(1)
        }, 400)
        return () => {
            if (mobileDebounceRef.current) clearTimeout(mobileDebounceRef.current)
        }
    }, [filterMobileNo])

    useEffect(() => {
        fetchLogs(page, limit, {
            module: filterModule,
            mobileNo: debouncedMobileNo,
            status: filterStatus,
        })
    }, [page, limit, filterModule, filterStatus, debouncedMobileNo, fetchLogs])

    const formatDateTime = (time) => {
        return time ? moment(time).utcOffset("+05:30").format("DD/MM/YYYY, hh:mm A") : "-";
    };

    const columns = [
        {
            title: "Module",
            dataIndex: "module",
            key: "module",
            render: (moduleText, record) => {
                if (moduleText && !["WhatsappWeb", "System Generated", "Vehicle", "null"].includes(moduleText)) {
                    return moduleText;
                }
                const text = record.text || "";
                if (/received at our workshop|track the service status|jobcard/i.test(text)) return "JobCard";
                if (/registered with Vehicle No|RC card|mParivahan|DigiLocker/i.test(text)) return "Registration";
                if (/Booking/i.test(text)) return "Booking";
                if (/number plate|plate/i.test(text)) return "NumberPlate";
                if (/chassis|invoice_no|invoice|sales invoice|sale invoice/i.test(text)) return "SaleInvoice";
                if (/due for service|service reminder|servicetype/i.test(text)) return "ServiceReminder";
                if (/Estimate Approval otp|OTP|verification code|reset your password/i.test(text)) return "Authentication";
                if (/enquir|quotation|e-brochure|generatePdf/i.test(text)) return "Quotation";
                return moduleText || "WhatsappWeb";
            }
        },
        {
            title: "Type",
            dataIndex: "type",
            key: "type",
        },
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
        },
        {
            title: "Contact Number",
            key: "destination",
            render: (_, record) => (
                <span>{record.destination && record.destination !== "System" ? record.destination : (record.source || "-")}</span>
            ),
        },
        {
            title: "Sent Date&Time",
            dataIndex: "sentTime",
            key: "sentTime",
            render: (text) => <span>{formatDateTime(text)}</span>,
        },
        {
            title: "Delivered Date&Time",
            dataIndex: "deliveredTime",
            key: "deliveredTime",
            render: (text) => <span>{formatDateTime(text)}</span>,
        },
        {
            title: "Read Date&Time",
            dataIndex: "readTime",
            key: "readTime",
            render: (text) => <span>{formatDateTime(text)}</span>,
        },
        {
            title: "Failed Date&Time",
            dataIndex: "failedTime",
            key: "failedTime",
            render: (text) => <span>{formatDateTime(text)}</span>,
        },
    ];

    const pageSizeMenu = (
        <Menu>
            {[10, 20, 50].map((menu) => (
                <Menu.Item
                    key={menu}
                    onClick={() => {
                        setLimit(menu)
                        setPage(1)
                    }}
                >
                    {menu}
                </Menu.Item>
            ))}
        </Menu>
    )

    const clearFilters = () => {
        setFilterModule(undefined)
        setFilterStatus(undefined)
        setFilterMobileNo("")
        setDebouncedMobileNo("")
        setPage(1)
    }

    return (
        <>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 16,
                    flexWrap: "wrap",
                }}
            >
                <h3 style={{ margin: 0, fontWeight: 600, whiteSpace: "nowrap" }}>
                    WhatsApp Logs [{count}]
                </h3>

                <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
                    <Select
                        allowClear
                        placeholder="Module"
                        style={{ width: 160, marginRight: 8 }}
                        value={filterModule}
                        onChange={(val) => {
                            setFilterModule(val)
                            setPage(1)
                        }}
                        showSearch
                        optionFilterProp="children"
                    >
                        {modules.map((m) => (
                            <Option key={m} value={m}>{m}</Option>
                        ))}
                    </Select>

                    <Input
                        allowClear
                        placeholder="Mobile No"
                        style={{ width: 150, marginRight: 8 }}
                        value={filterMobileNo}
                        onChange={(e) => setFilterMobileNo(e.target.value)}
                    />

                    <Select
                        allowClear
                        placeholder="Status"
                        style={{ width: 130, marginRight: 8 }}
                        value={filterStatus}
                        onChange={(val) => {
                            setFilterStatus(val)
                            setPage(1)
                        }}
                    >
                        {STATUS_OPTIONS.map((s) => (
                            <Option key={s.value} value={s.value}>{s.label}</Option>
                        ))}
                    </Select>

                    {(filterModule || filterStatus || filterMobileNo) && (
                        <Button style={{ marginRight: 8 }} onClick={clearFilters}>Clear</Button>
                    )}

                    <Dropdown overlay={pageSizeMenu} trigger={["click"]}>
                        <Button>
                            {limit} &nbsp;
                            <Icon type="down" />
                        </Button>
                    </Dropdown>
                </div>
            </div>

            <Table
                columns={columns}
                dataSource={data}
                rowKey={(record) =>
                    record.id ||
                    record.uid ||
                    `${record.destination || ""}-${record.sentTime || ""}-${(record.text || "").slice(0, 24)}`
                }
                loading={spinning}
                style={{ cursor: "pointer" }}
                pagination={{
                    current: page,
                    pageSize: limit,
                    total: count,
                    showQuickJumper: true,
                    showTotal: (total) => `Total ${total} logs`,
                    onChange: (p, pageSize) => {
                        setPage(p)
                        if (pageSize && pageSize !== limit) {
                            setLimit(pageSize)
                            setPage(1)
                        }
                    },
                }}
                onRow={(record) => ({
                    onClick: () => {
                        setVisible(true)
                        setSelectedMessage(record.text)
                    }
                })}
            />

            <WhatsappMessageModal
                message={selectedMessage}
                close={() => setVisible(false)}
                open={visible}
            />
        </>
    )
}

export default whatsappHistoryLogs
